const DISCLOSURE = 'details[data-disclosure]';

function closeDisclosure(details, { focus = false } = {}) {
  if (!details.open) return;
  if (details.animateClose) details.animateClose();
  else details.open = false;
  if (focus) details.querySelector('summary')?.focus();
}

document.addEventListener('click', (event) => {
  document.querySelectorAll(`${DISCLOSURE}[open]`).forEach((details) => {
    if (!details.contains(event.target)) closeDisclosure(details);
  });
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  const open = [...document.querySelectorAll(`${DISCLOSURE}[open]`)];
  const innermost = open.at(-1);
  if (innermost) closeDisclosure(innermost, { focus: true });
});

document.addEventListener(
  'toggle',
  (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.matches(DISCLOSURE) || !details.open) return;
    document.querySelectorAll(`${DISCLOSURE}[open]`).forEach((other) => {
      if (other !== details && !other.contains(details)) other.open = false;
    });
  },
  true
);

document.addEventListener('change', (event) => {
  const form = event.target.closest?.('form[data-localization-select]');
  if (form && event.target.matches('select')) form.submit();
});

class AnnouncementBar extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-announcement]')];
    if (this.slides.length < 2) return;

    this.index = 0;
    this.slides.forEach((slide, i) => (slide.hidden = i !== 0));
    this.interval = Number(this.dataset.interval || 5) * 1000;

    this.querySelector('[data-announcement-prev]')?.addEventListener('click', () => this.go(-1));
    this.querySelector('[data-announcement-next]')?.addEventListener('click', () => this.go(1));

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.addEventListener('mouseenter', () => this.stop());
    this.addEventListener('mouseleave', () => this.start());
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', () => this.start());
    this.start();
  }

  disconnectedCallback() {
    this.stop();
  }

  start() {
    this.stop();
    this.timer = window.setInterval(() => {
      if (this.dataset.layout === 'inline' && window.matchMedia('(min-width: 990px)').matches) return;
      this.go(1);
    }, this.interval);
  }

  stop() {
    window.clearInterval(this.timer);
  }

  go(step) {
    const current = this.slides[this.index];
    this.index = (this.index + step + this.slides.length) % this.slides.length;
    const next = this.slides[this.index];

    current.hidden = true;
    next.hidden = false;
    next.classList.remove('is-entering-next', 'is-entering-prev');
    void next.offsetWidth;
    next.classList.add(step > 0 ? 'is-entering-next' : 'is-entering-prev');
  }
}

if (!customElements.get('announcement-bar')) customElements.define('announcement-bar', AnnouncementBar);

class StickyHeader extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section') || this;
    this.drawer = this.querySelector('.menu-drawer');

    this.setHeight();
    this.resizeObserver = new ResizeObserver(() => this.setHeight());
    this.resizeObserver.observe(this);

    this.onDrawerToggle = () => this.setDrawerTop();
    this.drawer?.addEventListener('toggle', this.onDrawerToggle);

    if (this.drawer) {
      this.drawer.animateClose = () => this.closeDrawer();
      this.drawer.querySelector('summary')?.addEventListener('click', (event) => {
        if (!this.drawer.open) {
          this.setDrawerTop(true);
          return;
        }
        event.preventDefault();
        this.closeDrawer();
      });
    }

    if (this.dataset.sticky !== 'true') return;
    this.section.classList.add('header-section--sticky');

    this.compact = window.matchMedia('(max-width: 989px)');
    this.onScroll = () => this.updateVisibility();
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.compact.addEventListener('change', this.onScroll);
    this.updateVisibility();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.drawer?.removeEventListener('toggle', this.onDrawerToggle);
    if (!this.onScroll) return;
    window.removeEventListener('scroll', this.onScroll);
    this.compact.removeEventListener('change', this.onScroll);
  }

  updateVisibility() {
    if (this.drawer?.open || document.querySelector('dialog[open]')) return;
    const y = Math.max(window.scrollY, 0);
    const condensed = this.section.classList.contains('is-condensed');
    if (!this.compact.matches) {
      if (condensed) this.section.classList.remove('is-condensed');
      return;
    }
    if (!condensed && y > 160) this.section.classList.add('is-condensed');
    else if (condensed && y < 60) this.section.classList.remove('is-condensed');
  }

  setHeight() {
    document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
  }

  setDrawerTop(opening = false) {
    if (!this.drawer || (!this.drawer.open && !opening)) return;
    const bar = window.matchMedia('(max-width: 989px)').matches ? this.querySelector('.header__inner') : this;
    const bottom = Math.max((bar || this).getBoundingClientRect().bottom, 0);
    this.style.setProperty('--drawer-top', `${bottom}px`);

    const toggle = this.drawer.querySelector('summary').getBoundingClientRect();
    this.style.setProperty('--burger-x', `${toggle.left + toggle.width / 2}px`);
  }

  closeDrawer() {
    const drawer = this.drawer;
    if (!drawer?.open || drawer.classList.contains('is-closing')) return;

    const panel = drawer.querySelector('.menu-drawer__panel');
    if (!panel || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      drawer.open = false;
      return;
    }

    const finish = () => {
      clearTimeout(fallback);
      panel.removeEventListener('animationend', onEnd);
      drawer.classList.remove('is-closing');
      drawer.open = false;
    };
    const onEnd = (event) => {
      if (event.target === panel) finish();
    };
    const fallback = setTimeout(finish, 600);

    panel.addEventListener('animationend', onEnd);
    drawer.classList.add('is-closing');
  }
}

if (!customElements.get('sticky-header')) customElements.define('sticky-header', StickyHeader);

document.querySelectorAll('.header__menu').forEach((menu) => {
  let current = null;
  let openTimer;
  let closeTimer;
  let openedAt = 0;

  const close = () => {
    if (current?.contains(document.activeElement)) document.activeElement.blur();
    current?.classList.remove('is-open');
    current = null;
  };

  const open = (item) => {
    window.clearTimeout(closeTimer);
    if (current === item) return;
    current?.classList.remove('is-open');
    current = item;
    item?.classList.add('is-open');
    openedAt = window.scrollY;
  };

  // A little scroll is fine; past that the menu gets out of the way.
  window.addEventListener(
    'scroll',
    () => {
      if (current && Math.abs(window.scrollY - openedAt) > 100) {
        window.clearTimeout(openTimer);
        close();
      }
    },
    { passive: true }
  );

  menu.querySelectorAll(':scope > .header__menu-item').forEach((item) => {
    const hasMega = item.classList.contains('has-mega');

    item.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse') return;
      window.clearTimeout(openTimer);
      if (hasMega && current === item) return open(item);
      openTimer = window.setTimeout(() => (hasMega ? open(item) : close()), current ? 220 : 60);
    });

    item.addEventListener('pointerleave', (event) => {
      if (event.pointerType !== 'mouse') return;
      window.clearTimeout(openTimer);
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(close, 350);
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') close();
  });
});

class PromoSlider extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('.promo-slider__slide')];
    this.dotsContainer = this.querySelector('.promo-slider__dots');
    this.index = 0;
    this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === 0));
    if (this.slides.length < 2) return;

    this.dots = this.slides.map((_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'promo-slider__dot';
      dot.setAttribute('aria-label', String(i + 1));
      dot.addEventListener('click', () => {
        this.go(i);
        this.restart();
      });
      this.dotsContainer?.append(dot);
      return dot;
    });
    this.dotsContainer?.removeAttribute('aria-hidden');
    this.go(0);

    this.addEventListener('pointerdown', (event) => {
      this.startX = event.clientX;
    }, { passive: true });

    this.addEventListener('pointerup', (event) => {
      if (this.startX == null) return;
      const delta = event.clientX - this.startX;
      this.startX = null;
      if (Math.abs(delta) < 40) return;
      this.go(this.index + (delta < 0 ? 1 : -1));
      this.restart();
    });

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!this.reducedMotion) this.start();
  }

  disconnectedCallback() {
    this.stop();
  }

  start() {
    this.stop();
    this.timer = window.setInterval(() => this.go(this.index + 1), 5000);
  }

  stop() {
    window.clearInterval(this.timer);
  }

  restart() {
    if (!this.reducedMotion) this.start();
  }

  go(target) {
    const count = this.slides.length;
    this.index = ((target % count) + count) % count;
    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', String(!active));
      slide.querySelector('a')?.setAttribute('tabindex', active ? '0' : '-1');
    });
    this.dots?.forEach((dot, i) => dot.classList.toggle('is-active', i === this.index));
  }
}

if (!customElements.get('promo-slider')) customElements.define('promo-slider', PromoSlider);

const ACCORDION_EASING = 'cubic-bezier(0.2, 0.8, 0.2, 1)';

function animateSubmenu(details, open) {
  const panel = details.querySelector('.menu-drawer__subpanel');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  details.animation?.cancel();

  if (!panel || reduced) {
    details.open = open;
    return;
  }

  if (open) {
    details.open = true;
    const height = panel.scrollHeight;
    details.animation = panel.animate(
      [
        { height: '0px', opacity: 0 },
        { height: `${height}px`, opacity: 1 },
      ],
      { duration: 420, easing: ACCORDION_EASING }
    );
  } else {
    const height = panel.offsetHeight;
    details.classList.add('is-closing');
    details.animation = panel.animate(
      [
        { height: `${height}px`, opacity: 1 },
        { height: '0px', opacity: 0 },
      ],
      { duration: 320, easing: ACCORDION_EASING }
    );
    details.animation.onfinish = () => {
      details.open = false;
      details.classList.remove('is-closing');
    };
  }
}

document.addEventListener('click', (event) => {
  const summary = event.target.closest('.menu-drawer__submenu > summary');
  if (!summary) return;
  event.preventDefault();

  const details = summary.parentElement;
  const opening = !details.open || details.classList.contains('is-closing');

  if (opening) {
    details
      .closest('.menu-drawer__list')
      ?.querySelectorAll('.menu-drawer__submenu[open]')
      .forEach((other) => other !== details && animateSubmenu(other, false));
  }

  animateSubmenu(details, opening);
});
